import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Pencil, ArrowLeft } from 'lucide-react';
import DualScrollTable from '../../../components/common/DualScrollTable';
import { supabase } from '../../../lib/supabase';
import { formatTextWithLinks } from '../../../utils/formatters';
import type { Project } from '../../../types/project';
import ProjectFormModal from '../../../components/Projects/ProjectFormModal';

type SortField = 'proj_comm' | 'proj_desc' | 'proj_details' | 'proj_id';

export default function ProjectsAdmin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('proj_comm');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: projects, isLoading } = useQuery({
    queryKey: ['admin', 'projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('proj_comm', { ascending: true });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      return data || [];
    }
  });

  const filteredProjects = projects?.filter(project => {
    if (searchQuery) {
      return project.proj_desc.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  const sortedProjects = React.useMemo(() => {
    if (!filteredProjects) return [];
    
    return [...filteredProjects].sort((a, b) => {
      const direction = sortDirection === 'asc' ? 1 : -1;
      
      switch (sortField) {
        case 'proj_comm':
          return direction * a.proj_comm.localeCompare(b.proj_comm);
        case 'proj_desc':
          return direction * a.proj_desc.localeCompare(b.proj_desc);
        case 'proj_details':
          return direction * a.proj_details.localeCompare(b.proj_details);
        case 'proj_id':
          return direction * a.proj_id.localeCompare(b.proj_id);
        default:
          return 0;
      }
    });
  }, [filteredProjects, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    setSortDirection(prev => sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortField(field);
  };

  const getSortIcon = (field: SortField) => {
    if (field !== sortField) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="flex-1 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage community projects and initiatives
            </p>
          </div>
          <button
          onClick={() => {
            setSelectedProject(undefined);
            setIsModalOpen(true);
          }}
            className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Project
          </button>
        </div>
      </div>

      <div className="relative">
        <input
          type="text"
          placeholder="Search project descriptions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
        />
        <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
      </div>

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <DualScrollTable>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Edit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button onClick={() => handleSort('proj_id')} className="flex items-center gap-1">
                    Project ID {getSortIcon('proj_id')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button onClick={() => handleSort('proj_comm')} className="flex items-center gap-1">
                    Community {getSortIcon('proj_comm')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button onClick={() => handleSort('proj_desc')} className="flex items-center gap-1">
                    Description {getSortIcon('proj_desc')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button onClick={() => handleSort('proj_details')} className="flex items-center gap-1">
                    Details {getSortIcon('proj_details')}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  </td>
                </tr>
              ) : sortedProjects.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    No projects found
                  </td>
                </tr>
              ) : (
                sortedProjects.map((project) => (
                  <tr key={project.proj_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedProject(project);
                          setIsModalOpen(true);
                        }}
                        className="text-primary hover:text-primary-dark"
                      >
                        <Pencil className="h-5 w-5" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {project.proj_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {project.proj_comm}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {project.proj_desc}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatTextWithLinks(project.proj_details)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </DualScrollTable>
      </div>

      <ProjectFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProject(undefined);
          setError(null);
        }}
        initialData={selectedProject}
        onSubmit={async (data) => {
          setError(null);
          setIsSubmitting(true);
          try {
            if (selectedProject) {
              // Update existing project
              const { error: updateError } = await supabase
                .from('projects')
                .update(data)
                .eq('proj_id', selectedProject.proj_id);
              
              if (updateError) throw updateError;
            } else {
              // Add new project
              // Generate a unique ID for the project using the simpler format: comm_id-date-nnn
              
              // Use the comm_id directly (it's already the community ID from the dropdown)
              const commId = data.proj_comm;
              
              // Format date as YYMMDD
              const now = new Date();
              const year = now.getFullYear().toString().slice(-2);
              const month = (now.getMonth() + 1).toString().padStart(2, '0');
              const day = now.getDate().toString().padStart(2, '0');
              const dateStr = `${year}${month}${day}`;
              
              // Generate a random 3-digit number
              const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
              
              // Create the project ID
              const projectId = `${commId}-${dateStr}-${randomNum}`;
              
              console.log('Generated project ID:', projectId);
              
              // Add the proj_id to the data
              const projectData = {
                proj_id: projectId,
                ...data
              };
              
              console.log('Inserting new project with data:', projectData);
              
              // Use upsert instead of insert to handle potential conflicts
              const { data: insertedData, error: insertError } = await supabase
                .from('projects')
                .upsert(projectData, { onConflict: 'proj_id' })
                .select();
              
              if (insertError) {
                console.error('Error inserting project:', insertError);
                console.error('Error details:', JSON.stringify(insertError, null, 2));
                throw insertError;
              }
              
              console.log('Successfully inserted project:', insertedData);
            }
            
            await queryClient.invalidateQueries({ queryKey: ['admin', 'projects'] });
            setIsModalOpen(false);
            setSelectedProject(undefined);
          } catch (err) {
            console.error('Error in project submission:', err);
            console.error('Error type:', typeof err);
            console.error('Error stringified:', JSON.stringify(err, null, 2));
            
            // Extract more detailed error message
            let errorMessage = 'An error occurred';
            
            if (err instanceof Error) {
              errorMessage = err.message;
            }
            
            // If it's a Supabase error with details
            if (typeof err === 'object' && err !== null) {
              // Log all properties of the error object
              Object.keys(err).forEach(key => {
                console.error(`Error property ${key}:`, (err as any)[key]);
              });
              
              if ('details' in err && typeof (err as any).details === 'string') {
                errorMessage += `: ${(err as any).details}`;
              }
              
              if ('hint' in err && typeof (err as any).hint === 'string') {
                errorMessage += ` (${(err as any).hint})`;
              }
              
              if ('message' in err && typeof (err as any).message === 'string') {
                errorMessage = (err as any).message;
              }
              
              if ('code' in err && typeof (err as any).code === 'string') {
                errorMessage += ` [Code: ${(err as any).code}]`;
              }
            }
            
            console.error('Final error message:', errorMessage);
            setError(errorMessage);
          } finally {
            setIsSubmitting(false);
          }
        }}
        isSubmitting={isSubmitting}
        error={error}
        title={selectedProject ? 'Edit Project' : 'Add Project'}
        submitLabel={selectedProject ? 'Save Changes' : 'Add Project'}
      />
    </div>
  );
}
